import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	Box, Button, Chip, Collapse, IconButton, Link,
	LinearProgress, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow,
	Tooltip, Typography,
} from '@mui/material'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { dsColorNew } from '@palette'
import { AppCardTitle, AppNote, AppTableSkeleton, secondaryButtonSx, AppInfoButton, AppEmptyLine, AppBulkEditAction } from '@components'
import { PlayArrow as PlayIcon, RefreshOutlined as RefreshIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon, WarningAmberRounded as WarningIcon } from 'Pages/DsRepricing/@components/icons'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import PageWrapper from 'Pages/@PageWrapper'
import ChangesDialog, { OUTCOME_STYLE } from 'Pages/DsRepricing/Executions/@components/ChangesDialog'
import PassSearchesDialog from 'Pages/DsRepricing/Executions/@components/PassSearchesDialog'
import { pricePointsLabel, searchesLabel } from 'Pages/DsRepricing/Page/@services/runNow'
import { executionsApi } from 'Pages/DsRepricing/Executions/@services/api'
import { strategiesApi } from 'Pages/DsRepricing/Page/@services/api'
import { fromServer as strategyFromServer } from 'Pages/DsRepricing/Page/@services/adapters'
import { RateShopsProvider, perStrategy, useTenantRateShops } from 'Pages/DsRepricing/Page/@hooks/useRateShops'
import { fmtInt } from 'Pages/DsRepricing/Page/@services/cadence'
import { fmtSlot } from 'Pages/DsRepricing/Executions/@services/slot'
import { arrivalOf } from 'Pages/DsRepricing/Executions/@services/arrival'
import { PUSH_STYLE } from 'Pages/DsRepricing/Executions/@services/pushStyle'
import { decisionsState, launchAllOutcome, passCounts, passProgress, pushArrivedLabel } from 'Pages/DsRepricing/Executions/@services/passCounts'
import useToast from '@hooks/useToast'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { ActionBar, Surface } from 'Pages/DsRepricing/Setup/@components/Surface'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { inputSx, codeSx } from 'Pages/DsRepricing/Setup/@components/formStyles'

/**
 * Executions — its own entry since the 28 re-cut (CU-869fhuhy6, UX D9/D10).
 *
 * One row per pass of the sweep, newest first. `repricing_sweep_run`
 * had been recording this since the sweep was built and nothing read
 * it: "how is it going" was a question only someone with a ClickHouse
 * client could answer.
 *
 * A pass is asked for by strategies now (`run.strategies`, each with
 * what it composed and what it shared); a pass before the migration
 * keeps the schedule rule's name as text with the chip "rule · before 28"
 * (criterion 41). The tenant's searches a day and the warning are the
 * engine's figures (`GET /strategies/rate-shops`, criterion 28: shared
 * searches discounted, the same number as the Strategies panel).
 *
 * Three numbers per pass, from three places, answering different
 * questions:
 *   searches · price points — did the configuration produce searches?
 *                             `composed` are searches (office × pickup
 *                             date, billed); `created` are price points
 *                             (office × pickup date × rental length ×
 *                             site, rows of NEW_SEARCH) — CU-869fm5ju2;
 *   price points finished   — are the scrapers done? (same rows)
 *   decisions               — did the engine act, and how?
 * Amber on the first means real trouble only: sites the launch path
 * dropped, or search POSTs that failed (`passCounts`).
 *
 * The middle one is allowed to be missing. Until
 * `NEW_SEARCH.id_repricing_execution` is indexed the count takes
 * minutes, so the server abandons it and says so; this draws "not
 * available" rather than a bar at zero, which would read as "nothing
 * has finished".
 */


/** `[{ id, name, composed, shared }]` of a pass, or null for a historical one (by a rule). */
function passStrategies(run) {
	if (!Array.isArray(run.strategies) || !run.strategies.length) return null
	return run.strategies.map(x => (typeof x === 'object' ? x : { id: x }))
}

/** The strategies of the tenant, once, to name and filter. */
function useStrategies() {
	const [state, setState] = useState({ items: [], loading: true })
	useEffect(() => {
		let alive = true
		strategiesApi.list()
			.then(rows => alive && setState({ items: rows.map(strategyFromServer), loading: false }))
			.catch(() => alive && setState({ items: [], loading: false }))
		return () => { alive = false }
	}, [])
	return state
}

export default function DsRepricingExecutionsPage() {
	return (
		<RateShopsProvider>
			<ExecutionsPage/>
		</RateShopsProvider>
	)
}

// A link in the app's blue inside a note or a line of text
const linkSx = { color: dsColorNew.appBlue, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

function ExecutionsPage() {
	const [state, setState] = useState({ loading: true, items: [], progressOk: true, decisionsOk: true })
	const [open, setOpen] = useState(null)
	const [changes, setChanges] = useState(null)   // { run, type } behind the popup
	const [searchesOf, setSearchesOf] = useState(null)   // the pass whose searches are open (CU-869fmm943)
	// `?execution_id=` — landing on a pass from Real time (CU-869fmm943, UX Obs. 12-13, 19): read once
	// when the list has loaded, then removed from the URL so a reload does not jump again.
	const [searchParams, setSearchParams] = useSearchParams()
	const arriving = useRef(searchParams.get('execution_id'))
	const [arrival, setArrival] = useState(null)   // { openId, prepend, note }
	const [highlight, setHighlight] = useState(null)
	const highlightRef = useRef(null)
	const [strategyFilter, setStrategyFilter] = useState('')
	const [whyFilter, setWhyFilter] = useState('')
	const { items: strategies, loading: loadingStrategies } = useStrategies()
	const shops = useTenantRateShops()
	const threshold = shops.data?.warning ?? 0
	const total = shops.data?.grouped_total ?? null
	const [confirm, confirmDialog] = useConfirm()
	const { showToast } = useToast()
	const [launching, setLaunching] = useState(false)

	const nameOf = useMemo(() => new Map(strategies.map(s => [String(s.id), s.name])), [strategies])
	const on = strategies.filter(s => s.enabled)
	const perDay = (s) => perStrategy(shops.data, s.id)?.searches_per_day

	const load = useCallback(async () => {
		setState(s => ({ ...s, loading: true }))
		try {
			const body = await executionsApi.list({ limit: 30, strategyId: strategyFilter || null })
			setState({ loading: false, items: body?.items || [],
			           progressOk: body?.progress_ok !== false,
			           decisionsOk: body?.decisions_ok !== false, error: null })
		} catch (e) {
			setState({ loading: false, items: [], progressOk: true, decisionsOk: true,
			           error: e?.message || 'Could not load the executions' })
		}
	}, [strategyFilter])

	useEffect(() => { load() }, [load])

	useEffect(() => {
		const id = arriving.current
		if (!id || state.loading || state.error) return
		const consume = (result) => {
			arriving.current = null
			setArrival(result)
			if (result?.openId) { setOpen(result.openId); setHighlight(result.openId) }
			const next = new URLSearchParams(searchParams)
			next.delete('execution_id')
			setSearchParams(next, { replace: true })
		}
		const first = arrivalOf(state.items, id, undefined)
		if (!first.fetch) return consume(first)
		executionsApi.list({ limit: 1, executionId: id })
			.then(body => consume(arrivalOf(state.items, id, body?.items?.[0] || null)))
			.catch(() => consume(arrivalOf(state.items, id, null)))
	}, [state.loading, state.error, state.items])  // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (highlight && highlightRef.current) highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
	}, [highlight, state.items, arrival])

	const visible = state.items.filter(run => {
		if (whyFilter && (run.reason === 'force' ? 'force' : 'scheduled') !== whyFilter) return false
		return true
	})

	// Criterion 26: a pass of every strategy that is on, now, with the engine's count as the preview.
	const runNow = async () => {
		if (!await confirm({
			title: `Run now · ${on.length} strateg${on.length === 1 ? 'y' : 'ies'} on?`,
			body: `Every strategy that is on launches the searches it asks for right now, outside its run hours: ${on.map(s => s.name).join(', ') || 'none is on'}.`,
			warning: total != null ? `About ${fmtInt(total)} searches will be created (shared ones once), and searches are billed.` : 'Searches are billed. The engine will say how many before it launches them.',
			confirmLabel: total != null ? `Run now · ${fmtInt(total)} searches` : 'Run now',
			disabled: on.length === 0,
		})) return
		setLaunching(true)
		try {
			const r = await executionsApi.runNow('Run now from Executions')
			const outcome = launchAllOutcome(r)
			if (outcome.ok) showToast.success(outcome.text)
			else showToast.info(outcome.text)
			load()
		} catch (e) {
			showToast.error(e?.message || 'Could not launch the pass')
		} finally {
			setLaunching(false)
		}
	}

	const strategiesOn = `${on.length} strateg${on.length === 1 ? 'y' : 'ies'} on`

	return (
		<PageWrapper
			title={DS_REPRICING_ROUTES.EXECUTIONS_TITLE}
			// What the page is for, behind an ⓘ beside the subtitle, as Setup and Strategies
			subtitle={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
				{DS_REPRICING_ROUTES.EXECUTIONS_SUBTITLE}
				<AppInfoButton title={DS_REPRICING_ROUTES.EXECUTIONS_TITLE} size={18}>
					A pass launches the searches every strategy that is on asks for at that hour; the engine
					decides once each search comes back.
				</AppInfoButton>
			</Box>}
		>
			{/* The tenant total of criterion 28: the engine's number, shared searches discounted — the same one the Rate shops panel of Strategies shows. */}
			{!loadingStrategies && !shops.loading && (total != null && threshold > 0 && total > threshold ? (
				<AppNote severity="warning" sx={{ mb: 2 }}>
					<strong>{fmtInt(total)} searches a day across {strategiesOn}</strong> — above the {fmtInt(threshold)} warning set in
					the Rate shops panel of Strategies. {on.map(s => `${s.name} asks for ${perDay(s) == null ? '—' : fmtInt(perDay(s))}`).join(', ')}. Searches are billed.{' '}
					<Box component={RouterLink} to={DS_REPRICING_ROUTES.STRATEGIES_PATH} sx={linkSx}>Review the threshold</Box> ·{' '}
					<Box component={RouterLink} to={DS_REPRICING_ROUTES.STRATEGIES_PATH} sx={linkSx}>See the strategies</Box>
				</AppNote>
			) : (
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mb: 2 }}>
					{total != null
						? `${fmtInt(total)} searches a day across ${strategiesOn}${threshold > 0 ? ` — under the ${fmtInt(threshold)} warning` : ''} (the engine's count, shared searches counted once).`
						: shops.error
							? `${strategiesOn} · the engine did not answer with the searches / day.`
							: `${strategiesOn} · counting the searches / day…`}
				</Typography>
			))}

			<ActionBar sx={setupScopeSx}>
				<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
					<Select size="small" value={strategyFilter} displayEmpty onChange={e => setStrategyFilter(e.target.value)}
					        sx={{ ...inputSx, minWidth: 200, fontSize: 13 }}>
						<MenuItem value="">Any strategy</MenuItem>
						{strategies.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
					</Select>
					<Select size="small" value={whyFilter} displayEmpty onChange={e => setWhyFilter(e.target.value)}
					        sx={{ ...inputSx, minWidth: 140, fontSize: 13 }}>
						<MenuItem value="">Why · any</MenuItem>
						<MenuItem value="scheduled">scheduled</MenuItem>
						<MenuItem value="force">by hand</MenuItem>
					</Select>
				</Box>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 14 }}/>}
					        onClick={load} disabled={state.loading}
					        sx={secondaryButtonSx}>
						Refresh
					</Button>
					<AppBulkEditAction startIcon={<PlayIcon sx={{ fontSize: 14 }}/>} onClick={runNow}
					                   disabled={launching || loadingStrategies}>
						{launching ? 'Launching…' : 'Run now · all strategies on'}
					</AppBulkEditAction>
				</Box>
			</ActionBar>

			<Surface sx={{ p: 2.5, overflow: 'visible', ...setupScopeSx }}>
				<Box sx={{ mb: 2 }}>
					<AppCardTitle icon="list-check" title="Executions">
						<AppInfoButton title="Executions" sx={{ ml: 0.25 }}>
							Every pass of the sweep, newest first. A pass launches the searches the
							strategies that are on ask for; the engine decides once each search
							comes back.
						</AppInfoButton>
					</AppCardTitle>
				</Box>

				{state.error && (
					<AppNote severity="error" sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
							<span>{state.error}</span>
							<Button size="small" variant="outlined" onClick={load} sx={{ ...secondaryButtonSx, py: 0.25 }}>Retry</Button>
						</Box>
					</AppNote>
				)}

				{!state.progressOk && !state.error && (
					<AppNote sx={{ mb: 2 }}>
						Search progress is not available yet: counting them needs an index
						on <code>NEW_SEARCH</code> that has not been created. Everything
						else on this screen is live.
					</AppNote>
				)}

				{arrival?.note && <AppNote sx={{ mb: 2 }}>{arrival.note}</AppNote>}

				{state.loading && !state.items.length ? <AppTableSkeleton rows={6} columns={8}/> : (
					<Table size="small" sx={{
						'& tbody tr:nth-of-type(odd)':  { bgcolor: dsColorNew.white },
						'& tbody tr:nth-of-type(even)': { bgcolor: dsColorNew.tableRowAlt },
						'& td': { fontSize: 13, py: 1 },
					}}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: 34 }}/>
								<TableCell>When</TableCell>
								<TableCell>Strategies</TableCell>
								<TableCell align="center">Why</TableCell>
								<TableCell align="right">Searches · price points</TableCell>
								<TableCell sx={{ minWidth: 180 }}>Finished</TableCell>
								<TableCell>Decisions</TableCell>
								<TableCell sx={{ minWidth: 150 }}>PMS push</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!state.loading && state.items.length === 0 && !state.error && (
								<TableRow><TableCell colSpan={8} align="center" sx={{ py: 2 }}>
									<AppEmptyLine>
										{strategyFilter
											? 'No pass asked for by this strategy yet.'
											: 'No pass has run yet. A pass runs when a strategy that is on reaches one of its run hours; you can also launch one now from here, or from a strategy’s row.'}
									</AppEmptyLine>
								</TableCell></TableRow>
							)}
							{!state.loading && state.items.length > 0 && visible.length === 0 && (
								<TableRow><TableCell colSpan={8} align="center" sx={{ py: 2 }}>
									<AppEmptyLine>No pass matches the filter.</AppEmptyLine>
								</TableCell></TableRow>
							)}
							{(arrival?.prepend ? [arrival.prepend, ...visible] : visible).map((run, idx) => (
								<React.Fragment key={`${idx === 0 && arrival?.prepend ? 'arrived-' : ''}${run.id}`}>
									<Row run={run} progressOk={state.progressOk} decisionsOk={state.decisionsOk} nameOf={nameOf}
									     highlighted={highlight === run.id} rowRef={highlight === run.id ? highlightRef : undefined}
									     open={open === run.id}
									     onToggle={() => setOpen(open === run.id ? null : run.id)}
									     onOpenChanges={(type) => setChanges({ run, type })}
									     onOpenSearches={() => setSearchesOf(run)}/>
									<TableRow>
										<TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
											<Collapse in={open === run.id} unmountOnExit>
												<Detail run={run} onPushed={load} nameOf={nameOf}/>
											</Collapse>
										</TableCell>
									</TableRow>
									{idx === 0 && arrival?.prepend && (
										<TableRow>
											<TableCell colSpan={8} sx={{ py: 0.75, fontSize: 12, color: dsColorNew.textMuted,
											                             bgcolor: `${dsColorNew.white} !important` }}>
												The 30 most recent passes
											</TableCell>
										</TableRow>
									)}
								</React.Fragment>
							))}
						</TableBody>
					</Table>
				)}
			</Surface>
			{changes && (
				<ChangesDialog type={changes.type}
				               title={`${passTitle(changes.run, nameOf)} · ${fmtSlot(changes.run.slot)}`}
				               query={{ execution_id: changes.run.execution_id }}
				               activityHref={`${DS_REPRICING_ROUTES.ACTIVITY_PATH}?execution_id=${encodeURIComponent(changes.run.execution_id)}&event_type=${encodeURIComponent(changes.type)}`}
				               activityLabel="Open in Activity, filtered on this pass"
				               emptyText="Nothing of this kind in this pass."
				               onClose={() => setChanges(null)}/>
			)}
			{searchesOf && (
				<PassSearchesDialog run={searchesOf}
				                    title={`${passTitle(searchesOf, nameOf)} · ${fmtSlot(searchesOf.slot)}`}
				                    onClose={() => setSearchesOf(null)}/>
			)}
			{confirmDialog}
		</PageWrapper>
	)
}

const nameIn = (s, nameOf) => s.name || nameOf.get(String(s.id)) || `strategy ${s.id}`

function passTitle(run, nameOf) {
	const list = passStrategies(run)
	if (list) return list.map(s => nameIn(s, nameOf)).join(', ')
	return run.rule_name || 'pass'
}

/** The Strategies cell: stacked names, or the historical rule with its chip (criterion 41). */
function StrategiesCell({ run, nameOf }) {
	const list = passStrategies(run)
	if (list) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
				{list.map(s => <Box key={s.id} sx={{ fontWeight: 600 }}>{nameIn(s, nameOf)}</Box>)}
			</Box>
		)
	}
	if (run.rule_name) {
		return (
			<Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
				<Box component="span" sx={{ fontWeight: 600 }}>{run.rule_name}</Box>
				<Chip size="small" label="rule · before 28" title="A pass launched by a schedule rule, before the migration into strategies"
				      sx={toneChipSx('neutral')}/>
			</Box>
		)
	}
	return <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>
}

/**
 * "340 searches · 3,604 price points" (UX Obs. 1): the searches figure is
 * the way in to the searches of the pass — a real button, not clickable
 * at 0. The amber trouble moves to a ⚠ beside it with the same tooltip,
 * so link and warning never share one colour.
 */
function SearchesCell({ counts, onOpen }) {
	const searches = searchesLabel(counts.searches)
	return (
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap',
		                            columnGap: 0.5, '& > *': { whiteSpace: 'nowrap' } }}>
			{counts.searches > 0 ? (
				<Link component="button" type="button" onClick={onOpen} title="See the searches of this pass"
				      sx={{ fontSize: 'inherit', fontWeight: 600, color: dsColorNew.appBlue, verticalAlign: 'baseline',
				            textDecorationColor: 'transparent', '&:hover': { textDecorationColor: dsColorNew.appBlue } }}>
					{searches}
				</Link>
			) : <span>{searches}</span>}
			<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
				· {pricePointsLabel(counts.pricePoints)}
				{counts.warning && (
					<Tooltip title={counts.warning}>
						<Box component="span" aria-label={counts.warning} sx={{ display: 'inline-flex', color: dsColorNew.noteAmber }}>
							<WarningIcon sx={{ fontSize: 13 }}/>
						</Box>
					</Tooltip>
				)}
			</Box>
		</Box>
	)
}

function Row({ run, progressOk, decisionsOk, open, onToggle, onOpenChanges, onOpenSearches, nameOf, highlighted, rowRef }) {
	const decisions = run.decisions || {}
	const decided = decisionsState(run, decisionsOk)
	const counts = passCounts(run)
	return (
		<TableRow ref={rowRef}
		          sx={{ '&:hover': { bgcolor: dsColorNew.hoverBgLight },
		                ...(highlighted && { bgcolor: `${dsColorNew.pillActiveBg} !important`, boxShadow: `inset 3px 0 0 ${dsColorNew.appBlue}` }) }}>
			<TableCell>
				<IconButton size="small" onClick={onToggle}>
					{open ? <CollapseIcon fontSize="small"/> : <ExpandIcon fontSize="small"/>}
				</IconButton>
			</TableCell>
			<TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtSlot(run.slot)}</TableCell>
			<TableCell><StrategiesCell run={run} nameOf={nameOf}/></TableCell>
			<TableCell align="center">
				<Chip size="small" label={run.reason === 'force' ? 'by hand' : 'scheduled'}
				      sx={toneChipSx(run.reason === 'force' ? 'info' : 'neutral')}/>
			</TableCell>
			{/* Breakable between its two figures (each stays whole): at 1440 with the menu open the
			    one-line cell pushed the table past its card (QA 2026-10-27, already so on testing). */}
			<TableCell align="right" sx={{ fontFamily: 'inherit' }}>
				<SearchesCell counts={counts} onOpen={onOpenSearches}/>
			</TableCell>
			<TableCell><Progress run={run} progressOk={progressOk}/></TableCell>
			<TableCell>
				{decided.kind === 'unavailable' ? (
					<Typography title="The engine's decisions could not be read right now; reload in a moment."
					            sx={{ fontSize: 12, color: dsColorNew.textSubtle, fontStyle: 'italic' }}>
						not available
					</Typography>
				) : decided.kind === 'none' ? (
					<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle,
					                  fontStyle: 'italic' }}>
						nothing yet
					</Typography>
				) : (
					<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
						{Object.entries(decisions)
							.sort((a, b) => b[1] - a[1])
							.map(([type, n]) => {
								const s = OUTCOME_STYLE[type]
									|| { tone: 'neutral', label: type }
								return (
									<Chip key={type} size="small" label={`${s.label} ${n}`}
									      onClick={() => onOpenChanges(type)}
									      title={`What this pass ${s.label}: one row per decision`}
									      sx={toneChipSx(s.tone, { clickable: true })}/>
								)
							})}
					</Box>
				)}
			</TableCell>
			<TableCell><PushCell push={run.push}/></TableCell>
		</TableRow>
	)
}


function PushCell({ push }) {
	if (!push || !push.searches) {
		return (
			<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, fontStyle: 'italic' }}>
				no searches waiting
			</Typography>
		)
	}
	return (
		<Box>
			<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
				{Object.entries(PUSH_STYLE)
					.filter(([state]) => push[state])
					.map(([state, s]) => (
						<Chip key={state} size="small" label={`${s.label} ${push[state]}`}
						      sx={toneChipSx(s.tone)}/>
					))}
			</Box>
			<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, mt: 0.4 }}
			            title="Price points that reached the engine, of the ones the launch path created">
				{pushArrivedLabel(push)}
				{push.calls_ok ? ` · ${push.calls_ok} calls ok` : ''}
				{push.calls_error ? ` · ${push.calls_error} failed` : ''}
				{push.flush_count_max > 1 ? ` · sent ${push.flush_count_max}×` : ''}
			</Typography>
		</Box>
	)
}

function Progress({ run, progressOk }) {
	if (!progressOk || run.searches == null) {
		return (
			<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle,
			                  fontStyle: 'italic' }}>
				not available
			</Typography>
		)
	}
	const { pct, label } = passProgress(run)
	return (
		<Box>
			<LinearProgress variant="determinate" value={pct}
			                sx={{ height: 6, borderRadius: 3, mb: 0.4 }}/>
			<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted }}>
				{label}
			</Typography>
		</Box>
	)
}

function Detail({ run, onPushed, nameOf }) {
	const list = passStrategies(run)
	const skipped = Object.entries(run.skipped || {}).filter(([, n]) => n)
	const push = run.push || {}
	// Waiting or given up — or already sent with calls the PMS refused,
	// which Push now resends (ladders refused only).
	const canPush = (push.pending || 0) + (push.failed || 0) + (push.calls_error || 0) > 0
	const { showToast } = useToast()
	const [pushing, setPushing] = useState(false)
	const pushNow = async () => {
		setPushing(true)
		try {
			const r = await executionsApi.flush(run.execution_id, 'Push now from Executions')
			const sent = (r.sent || []).length, refused = (r.refused || []).length
			showToast.success(`Sent ${sent} search${sent === 1 ? '' : 'es'}${refused ? `, ${refused} refused` : ''}.`)
			onPushed?.()
		} catch (e) {
			showToast.error(e?.code === 'nothing_pending' ? 'Nothing is waiting on this pass.'
			              : e?.code === 'all_refused' ? 'The engine refused every search: somebody is already sending.'
			              : (e?.message || 'Push now failed'))
		} finally {
			setPushing(false)
		}
	}
	return (
		<Box sx={{ bgcolor: dsColorNew.tableRowAlt, px: 3, py: 2,
		           borderTop: '1px solid', borderColor: dsColorNew.grayBorder }}>
			{canPush && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, flex: 1 }}>
						{(push.pending || 0) + (push.failed || 0) > 0
							? `${push.pending || 0} search${(push.pending || 0) === 1 ? '' : 'es'} still waiting for the rest of their tasks${push.failed ? `, ${push.failed} given up` : ''}. Push now sends what has arrived; the brackets nobody decided keep the price the pricelist holds.`
							: `Every search was sent, but the PMS refused ${push.calls_error} call${push.calls_error === 1 ? '' : 's'}. Push now resends those ladders only.`}
						{(push.pending || 0) + (push.failed || 0) > 0 && push.calls_error ? ` ${push.calls_error} refused call${push.calls_error === 1 ? '' : 's'} are resent too.` : ''}
						{push.superseded ? ` ${push.superseded} other${push.superseded === 1 ? '' : 's'} went stale — a newer search for the same office and date was sent — and will not go.` : ''}
					</Typography>
					<AppBulkEditAction onClick={pushNow} disabled={pushing}>
						{pushing ? 'Sending…' : 'Push now'}
					</AppBulkEditAction>
				</Box>
			)}
			<Field label="Execution id" value={run.execution_id}/>
			<Field label="Started" value={fmtSlot(run.started_at)}/>
			<Field label="Finished" value={fmtSlot(run.finished_at)}/>
			<Box sx={{ mt: 1.5 }}>
				<Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
				                  color: dsColorNew.textMuted, mb: 0.75 }}>
					Strategies
				</Typography>
				{list ? list.map(s => (
					<Box key={s.id} sx={{ fontSize: 13, mb: 0.25 }}>
						<strong>{nameIn(s, nameOf)}</strong>
						{s.composed != null && <> — asked for <Box component="span" sx={codeSx}>{fmtInt(s.composed)}</Box> search{s.composed === 1 ? '' : 'es'}</>}
						{s.shared ? <>, {fmtInt(s.shared)} shared with another strategy</> : null}
					</Box>
				)) : (
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
						Historical pass{run.rule_name ? ` — launched by the schedule rule "${run.rule_name}"` : ''}, before the migration into
						strategies.
					</Typography>
				)}
			</Box>
			{run.error_message && (
				<AppNote severity="error" sx={{ mt: 1.5 }}>
					{run.error_message}
				</AppNote>
			)}
			{/* Already recorded by the sweep and never shown until now: a
			    pass that composed fewer searches than expected says why
			    here, one reason at a time. */}
			{skipped.length > 0 && (
				<Box sx={{ mt: 1.5 }}>
					<Typography sx={{ fontSize: 12, fontWeight: 700,
					                  textTransform: 'uppercase', letterSpacing: '0.05em',
					                  color: dsColorNew.textMuted, mb: 0.75 }}>
						Not searched
					</Typography>
					{skipped.map(([reason, n]) => (
						<Box key={reason} sx={{ display: 'flex', gap: 1, fontSize: 13 }}>
							<Box sx={{ fontFamily: 'inherit', fontWeight: 700,
							           minWidth: 40, textAlign: 'right' }}>{n}</Box>
							<Box sx={{ color: dsColorNew.textMuted }}>
								{reason.replace(/_/g, ' ')}
							</Box>
						</Box>
					))}
				</Box>
			)}
		</Box>
	)
}

function Field({ label, value }) {
	if (!value) return null
	return (
		<Box sx={{ display: 'flex', gap: 1, fontSize: 13, mb: 0.3 }}>
			<Box sx={{ minWidth: 110, color: dsColorNew.textMuted }}>{label}</Box>
			<Box sx={{ fontFamily: 'inherit' }}>{value}</Box>
		</Box>
	)
}
